import { useEffect, useRef } from 'react'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { isDevtoolsOpen, resetDevtoolsDetectionBaseline } from '@/utils/detectDevtools'

const DEVTOOLS_POLL_MS = 1000
const REPORT_DEBOUNCE_MS = 2000
/** Leituras consecutivas antes de registrar (evita falso positivo em resize/foco). */
const DEVTOOLS_CONFIRM_POLLS = 3

export type BehavioralProtectionMode = 'audit' | 'enforce'

type InspectionEventType =
  | 'devtools_open'
  | 'devtools_shortcut'
  | 'copy_blocked'
  | 'context_menu'
  | 'screenshot_attempt'

const lastReportAt: Partial<Record<InspectionEventType, number>> = {}

function reportSecurityEvent(eventType: InspectionEventType, detail?: string) {
  const now = Date.now()
  const last = lastReportAt[eventType] ?? 0
  if (now - last < REPORT_DEBOUNCE_MS) return
  lastReportAt[eventType] = now

  api
    .post('/security/track', {
      eventType,
      route: window.location.pathname + window.location.search,
      detail,
    })
    .catch(() => {
      // silencioso — proteção não deve quebrar UX
    })
}

function isPrintScreenKey(event: KeyboardEvent): boolean {
  return event.key === 'PrintScreen' || event.code === 'PrintScreen' || event.keyCode === 44
}

function isScreenshotShortcut(event: KeyboardEvent): boolean {
  if (isPrintScreenKey(event)) return true
  const key = event.key
  if (event.metaKey && event.shiftKey && (key === '3' || key === '4' || key === '5')) return true
  return false
}

function isDevtoolsShortcut(event: KeyboardEvent): boolean {
  const key = event.key
  if (!key) return false
  const normalized = key.toLowerCase()
  return (
    key === 'F12' ||
    (event.ctrlKey && event.shiftKey && (normalized === 'i' || normalized === 'j' || normalized === 'c')) ||
    (event.metaKey && event.altKey && (normalized === 'i' || normalized === 'j' || normalized === 'c'))
  )
}

/**
 * Proteção comportamental: registra DevTools, atalhos, prints e menu de contexto.
 * - audit: apenas registra (CRM interno)
 * - enforce: bloqueia cópia/menu e desloga ao abrir DevTools (portal PJ)
 */
export function useBehavioralProtection(
  enabled: boolean,
  mode: BehavioralProtectionMode = 'audit',
) {
  const logout = useAuthStore((s) => s.logout)
  const logoutTriggeredRef = useRef(false)
  const logoutRef = useRef(logout)
  const devtoolsHitsRef = useRef(0)
  logoutRef.current = logout

  useEffect(() => {
    if (!enabled) return

    const enforce = mode === 'enforce'

    const forceLogoutForDevtools = (detail: string) => {
      if (!enforce || logoutTriggeredRef.current) return
      logoutTriggeredRef.current = true
      reportSecurityEvent('devtools_open', detail)
      logoutRef.current()
    }

    const handleKeyboardSecurity = (event: KeyboardEvent) => {
      if (isDevtoolsShortcut(event)) {
        if (enforce && event.type === 'keydown') {
          event.preventDefault()
          event.stopImmediatePropagation()
          reportSecurityEvent('devtools_shortcut', event.key)
          forceLogoutForDevtools(`shortcut:${event.key}`)
          return
        }
        reportSecurityEvent('devtools_shortcut', `${event.type}:${event.key}`)
      }

      if (isScreenshotShortcut(event) && event.type === 'keydown') {
        reportSecurityEvent('screenshot_attempt', event.key || event.code)
      }
    }

    const onContextMenu = (event: MouseEvent) => {
      if (enforce) event.preventDefault()
      reportSecurityEvent('context_menu')
    }

    const onCopy = (event: ClipboardEvent) => {
      if (enforce) event.preventDefault()
      reportSecurityEvent('copy_blocked')
    }

    const onCut = (event: ClipboardEvent) => {
      if (enforce) event.preventDefault()
      reportSecurityEvent('copy_blocked', 'cut')
    }

    const checkDevtools = () => {
      if (!isDevtoolsOpen()) {
        devtoolsHitsRef.current = 0
        return
      }

      devtoolsHitsRef.current += 1
      if (devtoolsHitsRef.current < DEVTOOLS_CONFIRM_POLLS) return

      if (enforce) {
        forceLogoutForDevtools('detected')
      } else {
        reportSecurityEvent('devtools_open', 'detected')
      }
    }

    let resizeRecalibrateTimer: number | undefined
    const onResize = () => {
      window.clearTimeout(resizeRecalibrateTimer)
      resizeRecalibrateTimer = window.setTimeout(() => {
        resetDevtoolsDetectionBaseline()
        devtoolsHitsRef.current = 0
        checkDevtools()
      }, 400)
    }

    document.addEventListener('contextmenu', onContextMenu)
    document.addEventListener('copy', onCopy)
    document.addEventListener('cut', onCut)
    document.addEventListener('keydown', handleKeyboardSecurity, true)
    document.addEventListener('keyup', handleKeyboardSecurity, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('focus', checkDevtools)
    document.addEventListener('visibilitychange', checkDevtools)

    checkDevtools()
    const interval = window.setInterval(checkDevtools, DEVTOOLS_POLL_MS)

    return () => {
      document.removeEventListener('contextmenu', onContextMenu)
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('cut', onCut)
      document.removeEventListener('keydown', handleKeyboardSecurity, true)
      document.removeEventListener('keyup', handleKeyboardSecurity, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('focus', checkDevtools)
      document.removeEventListener('visibilitychange', checkDevtools)
      window.clearTimeout(resizeRecalibrateTimer)
      window.clearInterval(interval)
      devtoolsHitsRef.current = 0
    }
  }, [enabled, mode])
}
